import { useEffect, useRef, useState } from 'react';
import { type CustomRangeBounds, type RangeKey } from '@busy/api';
import { useT } from '../../lib/i18n';
import { CustomRangeSheet } from './CustomRangeSheet';

// Preset chips in display order. 'custom' is always the LAST option so
// the default UX stays simple — a barber who never opens the dropdown
// sees the same six presets they always did, plus a "Custom…" footer.
const PRESETS: RangeKey[] = ['today', '7d', '14d', '30d', '90d', 'ytd'];

// Dropdown-style range selector. Drives every dashboard query — change the range,
// metric grid + chart + insights all refetch via the [range] dep.
export function RangeSelector({
  value,
  customBounds,
  onChange,
}: {
  value: RangeKey;
  /** Active bounds when value === 'custom'. Pass null otherwise. */
  customBounds?: CustomRangeBounds | null;
  /** Called with the new range. For 'custom', the second arg carries
   *  the bounds the picker returned (already clamped). */
  onChange: (next: RangeKey, custom?: CustomRangeBounds | null) => void;
}) {
  const [open, setOpen] = useState(false);
  // Range sheet visibility — separate from `open` so the picker can
  // stay up after the dropdown closes (user picks "Custom…", dropdown
  // collapses, picker shows).
  const [showCustomSheet, setShowCustomSheet] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const t = useT();

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    // `ml-auto` keeps the selector right-aligned even when the parent
    // flex header wraps to two lines on narrow phones. Without this,
    // a wrapped trigger flows to the LEFT side of its new row and the
    // dropdown (anchored `right-0` below) extends 176px further left
    // — straight off the viewport's left edge on an iPhone.
    <div className="relative ml-auto" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-line bg-surface text-sm font-semibold hover:bg-bg-alt transition-colors"
      >
        <span>{t(`range.${value}`)}</span>
        <ChevronIcon className={`w-3.5 h-3.5 text-ink-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        // Explicit text color so we never inherit a stale color from a parent.
        <div className="absolute right-0 top-12 w-48 rounded-lg bg-surface text-ink-900 border border-line shadow-xl z-30 overflow-hidden">
          {PRESETS.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => {
                onChange(key, null);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-bg-alt ${
                key === value ? 'font-bold text-navy-900 dark:text-gold-500' : 'text-ink-700'
              }`}
            >
              {t(`range.${key}`)}
              {key === value && <CheckIcon className="w-4 h-4" />}
            </button>
          ))}
          {/* Custom… — always last so the default UX stays the simple
              preset list. Tap closes the dropdown and opens the date
              picker sheet. */}
          <div className="border-t border-line">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setShowCustomSheet(true);
              }}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-bg-alt ${
                value === 'custom' ? 'font-bold text-navy-900 dark:text-gold-500' : 'text-ink-700'
              }`}
            >
              <span>{t('range.custom')}</span>
              {value === 'custom' ? (
                <CheckIcon className="w-4 h-4" />
              ) : (
                <span className="text-ink-500 text-[11px]">{t('range.custom.hint')}</span>
              )}
            </button>
          </div>
        </div>
      )}
      {showCustomSheet && (
        <CustomRangeSheet
          initial={value === 'custom' ? customBounds ?? null : null}
          onApply={(bounds) => onChange('custom', bounds)}
          onClose={() => setShowCustomSheet(false)}
        />
      )}
    </div>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
